import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { client } from "../../sanity/client";
import { PortableText } from "@portabletext/react";
import NavigationBar from "../../Components/HighLevelComponents/NavigationBar/NavigationBar";

interface Tool {
  title: string;
  description: string;
  hero: {
    heading: string;
    subheading: string;
    image: {
      asset: {
        url: string;
      };
    };
    ctaButton: {
      text: string;
      link: string;
    };
  };
  keywords: string[];
  useCases: Array<{
    title: string;
    description: string;
    image: {
      asset: {
        url: string;
      };
    };
  }>;
  benefits: Array<{
    title: string;
    description: string;
    icon: {
      asset: {
        url: string;
      };
    };
  }>;
  mainContent: any[];
  features: Array<{
    title: string;
    description: string;
    icon: {
      asset: {
        url: string;
      };
    };
  }>;
  faq: Array<{
    question: string;
    answer: string;
  }>;
  seo: {
    metaTitle: string;
    metaDescription: string;
    ogImage: {
      asset: {
        url: string;
      };
    };
  };
}

const ToolLandingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [tool, setTool] = useState<Tool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTool = async () => {
      try {
        const query = `*[_type == "tool" && slug.current == $slug][0]{
          title,
          description,
          hero {
            heading,
            subheading,
            image {
              asset-> {
                url
              }
            },
            ctaButton {
              text,
              link
            }
          },
          keywords,
          useCases[] {
            title,
            description,
            image {
              asset-> {
                url
              }
            }
          },
          benefits[] {
            title,
            description,
            icon {
              asset-> {
                url
              }
            }
          },
          mainContent,
          features[] {
            title,
            description,
            icon {
              asset-> {
                url
              }
            }
          },
          faq[] {
            question,
            answer
          },
          seo {
            metaTitle,
            metaDescription,
            ogImage {
              asset-> {
                url
              }
            }
          }
        }`;

        type FetchFunction = (query: string, params: { slug: string }) => Promise<Tool>;
        const result = await (client.fetch as FetchFunction)(query, { slug: slug || '' });

        if (!result) {
          setError("Tool not found");
          return;
        }

        setTool(result);
      } catch (err) {
        setError("Error loading tool");
        console.error("Error fetching tool:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTool();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <NavigationBar />
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
          Loading...
        </div>
      </div>
    );
  }

  if (error || !tool) {
    return (
      <div className="min-h-screen bg-white">
        <NavigationBar />
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
          {error || "Tool not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <NavigationBar />
      {/* Hero Section */}
      <section className="relative bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {tool.hero.heading}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {tool.hero.subheading}
              </p>
              <a
                href={tool.hero.ctaButton.link}
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                {tool.hero.ctaButton.text}
              </a>
            </div>
            <div className="md:w-1/2">
              {tool.hero.image && (
                <img
                  src={tool.hero.image.asset.url}
                  alt={tool.title}
                  className="rounded-lg shadow-xl"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="prose max-w-none">
            <PortableText value={tool.mainContent} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tool.features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                {feature.icon && (
                  <img
                    src={feature.icon.asset.url}
                    alt={feature.title}
                    className="w-12 h-12 mb-4"
                  />
                )}
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Use Cases</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tool.useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                {useCase.image && (
                  <img
                    src={useCase.image.asset.url}
                    alt={useCase.title}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <h3 className="text-xl font-semibold mb-2">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tool.benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                {benefit.icon && (
                  <img
                    src={benefit.icon.asset.url}
                    alt={benefit.title}
                    className="w-12 h-12 mb-4"
                  />
                )}
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto">
            {tool.faq.map((item, index) => (
              <div key={index} className="mb-8">
                <h3 className="text-xl font-semibold mb-2">{item.question}</h3>
                <p className="text-gray-600">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ToolLandingPage;
